/** App-owned leave intents only. Shopify owns host history and the native confirmation UI. */
export type NavigationClient = { saveBar: { leaveConfirmation(): Promise<void>; hide(id: string): Promise<void> } }
type LeaveInput = {
  confirm: () => Promise<void>
  readDirty: () => boolean
  hideSaveBar: () => Promise<void>
  onFailure: (cause: unknown) => void
}
export function createShopifyLeaveGuard(input: LeaveInput) {
  let current = true
  type Intent = { navigate: () => void; resolve: (left: boolean) => void; finishing: boolean }
  let pendingIntent: Intent | null = null
  // Cancel the app destination independently of Shopify's still-open confirmation.
  let nativeConfirmation: Promise<void> | null = null
  const confirm = () => {
    if (nativeConfirmation) return nativeConfirmation
    // Preserve synchronous invocation, but observe synchronous provider throws as rejections too.
    const waiting = new Promise<void>((resolve) => {
      resolve(input.confirm())
    }).finally(() => {
      if (nativeConfirmation === waiting) nativeConfirmation = null
    })
    nativeConfirmation = waiting
    return waiting
  }
  const cancel = () => {
    const intent = pendingIntent
    pendingIntent = null
    intent?.resolve(false)
  }
  const finish = async (intent: Intent) => {
    if (!current || pendingIntent !== intent || intent.finishing) return
    intent.finishing = true
    try {
      // Hide while the element is still mounted. Passive React cleanup can run after navigation has detached it.
      if (!input.readDirty()) await input.hideSaveBar()
    } catch (cause) {
      if (!current || pendingIntent !== intent) return
      cancel()
      if (cause instanceof DOMException && cause.name === 'AbortError') return
      console.error('[shopify-navigation] Save Bar hide failed', cause)
      input.onFailure(cause)
      return
    }
    // Native confirmation and Save/Discard share this path. New typing or a different visit cancels the old intent.
    if (!current || pendingIntent !== intent) return
    pendingIntent = null
    if (input.readDirty()) {
      intent.resolve(false)
      return
    }
    try {
      intent.navigate()
      intent.resolve(true)
    } catch (cause) {
      console.error('[shopify-navigation] Destination failed', cause)
      intent.resolve(false)
      input.onFailure(cause)
    }
  }
  const failed = (intent: Intent, cause: unknown) => {
    if (!current || pendingIntent !== intent) return
    cancel()
    if (cause instanceof DOMException && cause.name === 'AbortError') return
    console.error('[shopify-navigation] Native leave failed', cause)
    input.onFailure(cause)
  }
  return {
    activate() {
      current = true
    },
    edited() {
      cancel()
    },
    settled() {
      if (pendingIntent && !input.readDirty()) void finish(pendingIntent)
    },
    dispose() {
      current = false
      cancel()
    },
    request(navigate: () => void): Promise<boolean> {
      if (!current) return Promise.resolve(false)
      if (!input.readDirty()) {
        cancel()
        return new Promise<boolean>((resolve) => {
          pendingIntent = { navigate, resolve, finishing: false }
          void finish(pendingIntent)
        })
      }
      if (pendingIntent) return Promise.resolve(false)
      // The app request can finish while Shopify's separate confirmation is still waiting.
      return new Promise<boolean>((resolve) => {
        const intent = { navigate, resolve, finishing: false }
        pendingIntent = intent
        void confirm().then(
          () => finish(intent),
          (cause) => failed(intent, cause),
        )
      })
    },
  }
}
export type ShopifyLeaveGuard = ReturnType<typeof createShopifyLeaveGuard>
// Store readers, never a second copy of form values or dirty state. The current editor owns registration.
let active: { client: NavigationClient; guard: ShopifyLeaveGuard } | null = null
export function registerShopifyLeaveGuard(client: NavigationClient, guard: ShopifyLeaveGuard) {
  if (active?.guard !== guard) active?.guard.dispose()
  guard.activate()
  const registration = { client, guard }
  active = registration
  return () => {
    guard.dispose()
    if (active === registration) active = null
  }
}
export async function requestShopifyNavigation(client: NavigationClient, navigate: () => void) {
  if (active?.client === client) return active.guard.request(navigate)
  await client.saveBar.leaveConfirmation()
  navigate()
  return true
}
